import { ChatListItem, ChatSearchParams } from '@choco-admin/types';
import { useCallback, useState, useMemo } from 'react';

import {
  type ChatSearchDataQuery,
  type ChatFilter,
  type SearchFilter,
  useChatSearchDataLazyQuery,
} from '../../../generated';
import { CHAT_USERS_PAGE_SIZE } from '../constants/chatConstants';
import { mapToChat } from '../utils/chatMapper';

export type CaptureExceptionFunction = (error: Error) => void;

export type UseSearchChatsResponse = {
  chats: ChatListItem[];
  totalChats: number;
  isLoading: boolean;
  hasNextPage: boolean;
  searchChats: (params: ChatSearchParams) => Promise<void>;
  refetch: () => Promise<void>;
};

export type UseSearchChatsConfig = {
  initialPageSize: number;
  captureException?: CaptureExceptionFunction;
};

type ChatSearchConnectionResult = ChatSearchDataQuery['search'];

const mapChats = (
  result: ChatSearchConnectionResult | null | undefined
): { chats: ChatListItem[]; total: number } => {
  if (!result?.hits) {
    return { chats: [], total: 0 };
  }

  try {
    const chatFragments = result.hits
      .map((hit) => hit.entity)
      .filter(
        (
          entity
        ): entity is Extract<
          NonNullable<typeof entity>,
          { __typename: 'Chat' }
        > => entity?.__typename === 'Chat'
      );

    const chats = chatFragments.map((fragment) => mapToChat(fragment));
    return { chats, total: result.total ?? 0 };
  } catch (error) {
    // Return empty result on mapping error
    return { chats: [], total: 0 };
  }
};

export const useSearchChats = (
  config: UseSearchChatsConfig
): UseSearchChatsResponse => {
  const { captureException, initialPageSize } = config;
  const [lastParams, setLastParams] = useState<ChatSearchParams | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(initialPageSize);

  const [executeQuery, { data, loading }] = useChatSearchDataLazyQuery({
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const searchChats = useCallback(
    async (params: ChatSearchParams) => {
      try {
        setLastParams(params);
        const { searchTerm, filter, page = 1, pageSize } = params;
        const effectivePageSize = pageSize ?? initialPageSize;
        const offset = (page - 1) * effectivePageSize;
        setCurrentPage(page);
        setCurrentPageSize(effectivePageSize);

        const chatFilter: ChatFilter = {};

        if (filter?.enabled !== undefined) {
          chatFilter.enabled = filter.enabled;
        }

        if (filter?.isPuppy !== undefined) {
          chatFilter.isPuppy = filter.isPuppy;
        }

        if (filter?.type && filter.type !== 'all') {
          chatFilter.type = filter.type;
        }

        // Wrap the chat filter in the SearchFilter structure
        const searchFilter: SearchFilter | undefined =
          Object.keys(chatFilter).length > 0 ? { chat: chatFilter } : undefined;

        await executeQuery({
          variables: {
            searchTerm,
            searchEntity: 'chat' as const,
            first: CHAT_USERS_PAGE_SIZE,
            size: effectivePageSize,
            offset,
            withAllUserFields: false,
            filter: searchFilter,
          },
        });
      } catch (error) {
        captureException?.(error as Error);
        throw error;
      }
    },
    [executeQuery, captureException, initialPageSize]
  );

  const refetch = useCallback(async () => {
    if (lastParams) {
      await searchChats(lastParams);
    }
  }, [lastParams, searchChats]);

  const { chats, total } = useMemo(
    () => mapChats(data?.search),
    [data?.search]
  );

  const hasNextPage = currentPage * currentPageSize < total;

  return useMemo(
    () => ({
      chats: chats || [],
      totalChats: total,
      isLoading: loading,
      hasNextPage,
      searchChats,
      refetch,
    }),
    [chats, total, loading, hasNextPage, searchChats, refetch]
  );
};
